import React from 'react';
import { Modal, Button, Table, Badge, Card, Spinner, Alert, Accordion, ProgressBar } from 'react-bootstrap';
import {
  FiAlertCircle
} from 'react-icons/fi';

const DashboardModals = ({
  selectedSO,
  showItemsModal, setShowItemsModal, selectedItems,
  showStockDetailModal, setShowStockDetailModal, selectedProduct,
  showReservedStockModal, setShowReservedStockModal, selectedReservedProduct, loadingReservedStock, reservedStockOrders,
  showBOMModal, setShowBOMModal, selectedItem, loadingBOM, bomList, stockSummary,
  handleCreatePurchaseRequest, handleCreateCommonPurchaseRequest, handleCreateFGPR,
  showProductionBatchModal, setShowProductionBatchModal, loadingBatches, productionBatches, handleAssignToBatch,
  handleRaiseProductionRequest
}) => {
  return (
    <>
      {/* Items Modal */}
      <Modal show={showItemsModal} onHide={() => setShowItemsModal(false)} size="lg">
        <Modal.Header closeButton><Modal.Title>Sales Order Items</Modal.Title></Modal.Header>
        <Modal.Body>
          <Table hover>
            <thead><tr><th>Product Name</th><th>Quantity</th><th>Quality</th><th>Unit Price</th><th>Total</th></tr></thead>
            <tbody>
              {selectedItems.map((item, index) => (
                <tr key={index}>
                  <td><strong>{item.product?.productName || 'N/A'}</strong><br /><small className="text-muted">{item.product?.productCode}</small></td>
                  <td>{item.quantity} {item.unit}</td>
                  <td><Badge bg="info">{item.quality || 'Standard'}</Badge></td>
                  <td>₹{item.unitPrice?.toLocaleString() || 0}</td>
                  <td>₹{item.totalPrice?.toLocaleString() || 0}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setShowItemsModal(false)}>Close</Button></Modal.Footer>
      </Modal>

      {/* Stock Detail Modal */}
      <Modal show={showStockDetailModal} onHide={() => setShowStockDetailModal(false)} size="lg">
        <Modal.Header closeButton><Modal.Title>Stock Details - {selectedProduct?.product?.productName}</Modal.Title></Modal.Header>
        <Modal.Body>
          {selectedProduct && (
            <Table hover>
              <thead><tr><th>Location</th><th>Available Stock</th><th>Storage Type</th><th>Last Updated</th></tr></thead>
              <tbody>
                {selectedProduct.stockByLocation?.map((location) => (
                  <tr key={location.warehouseLocation._id}>
                    <td>{location.warehouseLocation.label}</td>
                    <td><Badge bg="primary">{location.availableStock} {location.unit}</Badge></td>
                    <td>{location.warehouseLocation.isStorage ? <Badge bg="success">Storage</Badge> : <Badge bg="secondary">Non-Storage</Badge>}</td>
                    <td>{new Date(location.lastUpdated).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setShowStockDetailModal(false)}>Close</Button></Modal.Footer>
      </Modal>

      {/* BOM Modal */}
      <Modal show={showBOMModal} onHide={() => setShowBOMModal(false)} size="lg" >
        <Modal.Header closeButton>
          <Modal.Title>
            BOM Details – {selectedItem?.product?.productName}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* FG PR Section - Connected to Sales Order */}
          <h5>1. Raise Direct Finished Good Purchase Request</h5>
          <Card className="mb-4 border-primary">
            <Card.Body className="d-flex justify-content-between align-items-center">
              <div>
                <h6 className="mb-1">Finished Good PR</h6>
                <div className="text-muted small">Generate a Purchase Request for this finished good tied to the current Sales Order.</div>
              </div>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => handleCreateFGPR(selectedItem, selectedSO)}
              >
                Create FG PR ({selectedItem?.quantity} {selectedItem?.unit})
              </Button>
            </Card.Body>
          </Card>

          <h5>2. View BOMs and Related Production Batches</h5>
          {loadingBOM ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <p className="mt-2">Loading BOM details...</p>
            </div>
          ) : bomList.length === 0 ? (
            <Alert variant="info">
              <Alert.Heading>No BOM Configuration Found</Alert.Heading>
              <p>No Bill of Materials (BOM) is configured for this product.</p>
            </Alert>
          ) : (
            bomList.map((bom, idx) => {
              const rms = Array.isArray(bom.rawMaterials) ? bom.rawMaterials : [];

              // Match output product
              const matchingOutput = Array.isArray(bom.outputs)
                ? bom.outputs.find(
                  o => o.productId?._id === selectedItem?.product?._id
                )
                : null;

              // Yield factor (decimal: 0.65 = 65%)
              const yieldFactor = matchingOutput?.percentage ?? 1;

              // Total BOM raw material quantity
              const totalBomQty = rms.reduce(
                (sum, rm) => sum + Number(rm.quantity || 0),
                0
              );

              // Compute required quantities
              const computed = rms.map(rm => {
                const orderQty = selectedItem?.quantity || 1;
                const rmRatio = totalBomQty > 0 ? rm.quantity / totalBomQty : 0;
                const requiredQty = (orderQty * rmRatio) / (yieldFactor / 100);

                const availableStock =
                  stockSummary.find(
                    s =>
                      s.product._id ===
                      (rm.materialId?._id || rm.product?._id)
                  )?.totalAvailableStock || 0;

                const shortfall = Math.max(0, requiredQty - availableStock);

                return { rm, requiredQty, availableStock, shortfall };
              });

              const allAvailable =
                computed.length > 0 &&
                computed.every(c => c.availableStock >= c.requiredQty);

              const missingItems = computed.filter(c => c.shortfall > 0);


              // Filter running batches for this BOM
              const runningBatchesForBom = (productionBatches || []).filter(
                batch => {
                  const bId = batch.bomId?._id || batch.bomId;
                  return bId?.toString() === bom._id?.toString();
                }
              );

              // Check if current SO item is already assigned to any batch for this BOM
              const currentSOId = selectedSO?._id || selectedSO;
              const currentSOItemId = selectedItem?._id || selectedItem?.salesOrderItemId;

              const assignedBatch = runningBatchesForBom.find(batch =>
                batch.assignedSalesOrderItems?.some(item =>
                  item.salesOrderId === currentSOId?.toString() &&
                  item.salesOrderItemId === currentSOItemId?.toString()
                )
              );

              return (
                <Card key={idx} className="mb-4 shadow-sm border-0 rounded-4">
                  {/* HEADER */}
                  <Card.Header className="bg-white border-0 pb-0">
                    <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-2">

                      <div>
                        <h5 className="fw-semibold mb-1">
                          {bom.name || "BOM"}
                          <span className="text-muted ms-2 small">
                            #{bom.bomId || bom._id}
                          </span>
                        </h5>

                        <div className="d-flex gap-3 mt-2 small">
                          <span>
                            Total Items: <strong>{computed.length}</strong>
                          </span>
                          <span className={allAvailable ? "text-success" : "text-danger"}>
                            {allAvailable
                              ? "All Materials Available"
                              : `${missingItems.length} Item(s) in Shortfall`}
                          </span>
                        </div>
                      </div>
                      <div>
                        {allAvailable ? (
                          <Button
                            variant="success"
                            className="w-100 rounded-pill"
                            onClick={() => handleRaiseProductionRequest(bom)}
                          >
                            Raise Production Request
                          </Button>
                        ) : (
                          <Button
                            variant="warning"
                            className="w-100 rounded-pill"
                            onClick={() =>
                              handleCreateCommonPurchaseRequest(missingItems, selectedSO)
                            }
                          >
                            Generate Purchase Request First
                          </Button>
                        )}
                      </div>
                    </div>

                    <div className="production-actions border rounded-4 p-3 bg-light">
                      <div className="fw-semibold mb-1">
                        🔁 Assign to Running Batch
                      </div>

                      <div className="small text-muted mb-2">
                        Add this SO Item into an already running batch.
                      </div>

                      {assignedBatch ? (
                        <div className="alert alert-info py-2 px-3 mb-0 rounded-3 d-flex align-items-center gap-2">
                          <FiAlertCircle />
                          <div>
                            This item is already assigned to <strong>{assignedBatch.label || assignedBatch.batchNumber}</strong>
                          </div>
                        </div>
                      ) : runningBatchesForBom.length === 0 ? (
                        <div className="text-muted small">
                          No running batches available.
                        </div>
                      ) : (
                        <div className="d-flex flex-wrap gap-2 mt-2">

                          {runningBatchesForBom.map(batch => (
                            <Button
                              key={batch._id}
                              variant="outline-primary"
                              className="rounded-3 px-3 py-2 text-start"
                              // style={{ minWidth: "200px" }}
                              onClick={() => handleAssignToBatch(batch._id)}
                            >
                              <div className="fw-semibold">
                                {batch.label}
                              </div>

                              <div className="small text-muted">
                                Batch No: {batch.batchNumber}
                              </div>

                              {/* {batch.availableOutput && (
                                                        <div className="small text-success">
                                                          Available Output: {batch.availableOutput}
                                                        </div>
                                                      )} */}
                            </Button>
                          ))}
                        </div>
                      )}
                    </div>

                  </Card.Header>

                  {/* BODY */}
                  <Card.Body className="pt-3">

                    <Accordion>
                      <Accordion.Item eventKey="0">
                        <Accordion.Header>
                          View Raw Materials ({computed.length})
                        </Accordion.Header>

                        <Accordion.Body className="p-0">

                          {computed.map((c, i) => {
                            const percentage = Math.min(
                              (c.availableStock / c.requiredQty) * 100,
                              100
                            );

                            const isShort = c.shortfall > 0;

                            return (
                              <div
                                key={i}
                                className={`p-3 border-bottom ${isShort ? "bg-light-danger" : ""
                                  }`}
                              >
                                <div className="d-flex justify-content-between align-items-center">

                                  {/* LEFT SIDE */}
                                  <div>
                                    <div className="fw-medium">
                                      {c.rm.materialId?.productName ||
                                        c.rm.product?.productName ||
                                        "Unknown"}
                                    </div>

                                    <div className="text-muted small">
                                      Required: {c.requiredQty.toFixed(2)} {c.rm.unit}
                                    </div>
                                  </div>

                                  {/* RIGHT SIDE */}
                                  <div className="text-end">
                                    <div
                                      className={
                                        isShort ? "text-danger fw-semibold" : "text-success"
                                      }
                                    >
                                      Available: {c.availableStock} {c.rm.unit}
                                    </div>

                                    {isShort && (
                                      <Button
                                        variant="outline-danger"
                                        size="sm"
                                        className="rounded-pill mt-2"
                                        onClick={() =>
                                          handleCreatePurchaseRequest(
                                            c.rm,
                                            c.shortfall,
                                            selectedSO
                                          )
                                        }
                                      >
                                        Create PO ({c.shortfall.toFixed(2)} {c.rm.unit})
                                      </Button>
                                    )}
                                  </div>
                                </div>

                                {/* PROGRESS BAR */}
                                <ProgressBar
                                  now={percentage}
                                  className="mt-2"
                                  variant={isShort ? "danger" : "success"}
                                />
                              </div>
                            );
                          })}
                        </Accordion.Body>
                      </Accordion.Item>
                    </Accordion>
                  </Card.Body>
                </Card>
              );
            })
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBOMModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Production Batch Modal */}
      <Modal show={showProductionBatchModal} onHide={() => setShowProductionBatchModal(false)} size="lg">
        <Modal.Header closeButton><Modal.Title>Running Production Batches - {selectedItem?.product?.productName}</Modal.Title></Modal.Header>
        <Modal.Body>
          {loadingBatches ? (
            <div className="text-center py-4"><Spinner animation="border" /><p className="mt-2">Loading production batches...</p></div>
          ) : (
            <Table hover>
              <thead><tr><th>Batch #</th><th>Expected Output</th><th>Available Qty</th><th>Status</th><th>Action</th></tr></thead>
              <tbody>
                {productionBatches.length === 0 ? (
                  <tr><td colSpan="5" className="text-center py-4"><div className="text-muted">No available production batches found</div></td></tr>
                ) : (
                  productionBatches.map(batch => (
                    <tr key={batch._id}>
                      <td><strong>{batch.batchNumber}</strong><br /><small className="text-muted">{batch.bomName}</small></td>
                      <td>{batch.expectedOutput} {batch.unit}</td>
                      <td className="text-success">{batch.availableOutput} {batch.unit}</td>
                      <td><Badge bg="info">{batch.status}</Badge></td>
                      <td>
                        <Button
                          variant={batch.availableOutput >= selectedItem?.quantity ? "success" : "outline-warning"}
                          size="sm"
                          onClick={() => handleAssignToBatch(batch._id)}
                        >
                          {batch.availableOutput >= selectedItem?.quantity ? "Assign" : "Partial Assign"} ({Math.min(batch.availableOutput, selectedItem?.quantity)} {batch.unit})
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setShowProductionBatchModal(false)}>Close</Button></Modal.Footer>
      </Modal>
    </>
  );
};

export default DashboardModals;
